import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function prettyDate(yyyymmdd) {
  try {
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch (_e) { return yyyymmdd; }
}

function thisMonthYYYYMM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function lastMonthYYYYMM() {
  const d = new Date();
  d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const EMPTY_ENTRIES = () => [
  { grateful_for: "", action_taken: "" },
  { grateful_for: "", action_taken: "" },
  { grateful_for: "", action_taken: "" },
];

export default function GratitudeJournal({ session }) {
  const today = todayStr();
  const [entries, setEntries] = useState(EMPTY_ENTRIES());
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [streak, setStreak] = useState(0);
  const [celebration, setCelebration] = useState(null); // milestone num
  const [history, setHistory] = useState([]);
  const [emailingMonth, setEmailingMonth] = useState(false);
  const [monthlyMsg, setMonthlyMsg] = useState(null);

  // Load existing entry for today + last 60 days
  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bridge/gratitude`);
      const items = res.data?.items || [];
      setHistory(items);
      const todayItem = items.find((it) => it.date === today);
      if (todayItem && Array.isArray(todayItem.entries) && todayItem.entries.length) {
        const padded = [...todayItem.entries];
        while (padded.length < 3) padded.push({ grateful_for: "", action_taken: "" });
        setEntries(padded);
        setSavedAt(todayItem.updated_at || todayItem.created_at);
      }
    } catch (_err) { /* ignore */ }
  }, [today]);

  useEffect(() => { refresh(); }, [refresh]);

  // Compute current streak from history (consecutive days ending today)
  useEffect(() => {
    if (!history || history.length === 0) { setStreak(0); return; }
    const dates = new Set(
      history
        .filter((h) => Array.isArray(h.entries) && h.entries.some((e) => (e.grateful_for || "").trim()))
        .map((h) => h.date),
    );
    const t = new Date();
    let count = 0;
    for (let i = 0; i < 200; i++) {
      const yyyymmdd = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
      if (dates.has(yyyymmdd)) {
        count += 1;
        t.setDate(t.getDate() - 1);
      } else {
        break;
      }
    }
    setStreak(count);
  }, [history]);

  const updateEntry = (idx, field, value) => {
    setEntries((arr) => arr.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
    setSavedSuccess(false);
  };

  const save = async () => {
    const meaningful = entries.filter((e) => e.grateful_for.trim());
    if (meaningful.length === 0) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/bridge/gratitude`, { date: today, entries: meaningful });
      setSavedSuccess(true);
      setSavedAt(new Date().toISOString());
      if (res.data?.celebrated_milestone) {
        setCelebration(res.data.celebrated_milestone);
      }
      if (typeof res.data?.streak === "number") {
        setStreak(res.data.streak);
      }
      refresh();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Could not save your gratitude. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const emailMonthly = async (which = "auto") => {
    setEmailingMonth(true);
    setMonthlyMsg(null);
    try {
      // 'auto' lets the backend decide (current if has entries, else last month).
      // Frontend sends explicit month for the two specific options.
      let params = {};
      if (which === "current") params = { month: thisMonthYYYYMM() };
      else if (which === "last") params = { month: lastMonthYYYYMM() };
      const res = await axios.post(`${API}/bridge/gratitude/email-monthly`, null, { params });
      const insightNote = res.data?.has_insight ? " Includes a personalised reflection from Claude." : "";
      setMonthlyMsg({ kind: "ok", text: `Sent to ${session?.email}. ${res.data?.total_entries || 0} entries included.${insightNote}` });
    } catch (err) {
      setMonthlyMsg({ kind: "err", text: err?.response?.data?.detail || "Could not send monthly report." });
    } finally {
      setEmailingMonth(false);
    }
  };

  return (
    <div className="bridge-panel" data-testid="bridge-panel-gratitude">
      <h2 className="bridge-panel-heading">Your <em>Gratitude Journal</em></h2>
      <p className="bridge-panel-sub">Each entry goes deeper than "I'm grateful for…" — you'll trace the actions, mindset shifts, and openness that allowed each blessing in. This is gratitude rooted in agency.</p>

      {celebration && (
        <div className="streak-celebration" data-testid="streak-celebration">
          <div className="streak-celebration-num">{celebration}</div>
          <div className="streak-celebration-text">
            <strong>{celebration}-day streak unlocked! 🎉</strong>
            We just emailed you the celebration — check your inbox for what your nervous system has been quietly doing. Keep going.
          </div>
        </div>
      )}

      {streak >= 1 && !celebration && (
        <div className="streak-banner" data-testid="streak-banner">
          <div className="streak-banner-flame">{streak >= 7 ? "🔥" : "✦"}</div>
          <div className="streak-banner-text">
            <strong>{streak}-day streak</strong> — {streak < 7
              ? `${7 - streak} more ${7 - streak === 1 ? "day" : "days"} until your first milestone celebration unlocks.`
              : streak < 21
                ? `${21 - streak} more until the 21-day habit-threshold milestone.`
                : streak < 30
                  ? `${30 - streak} more until the 30-day milestone.`
                  : "You're in rare territory. Keep building the record."}
          </div>
        </div>
      )}

      <p className="grat-date">{prettyDate(today)} {savedAt && <span style={{ color: "var(--bridge-sage)", marginLeft: 8 }}>· last saved</span>}</p>

      <div className="grat-entries">
        {entries.map((e, i) => (
          <div className="grat-entry" key={i} data-testid={`grat-entry-${i}`}>
            <div className="grat-entry-num">{i + 1}</div>
            <label htmlFor={`gf-${i}`}>I am grateful for…</label>
            <textarea id={`gf-${i}`} value={e.grateful_for}
              onChange={(ev) => updateEntry(i, "grateful_for", ev.target.value)}
              placeholder={i === 0 ? "e.g. an afternoon of clarity in my work…" : "e.g. a healing conversation with someone I love…"}
              data-testid={`grat-grateful-${i}`} />
            <div className="grat-action">
              <label htmlFor={`act-${i}`}>And here's what I did to allow this in…</label>
              <textarea id={`act-${i}`} value={e.action_taken}
                onChange={(ev) => updateEntry(i, "action_taken", ev.target.value)}
                placeholder={i === 0 ? "e.g. I closed my laptop at lunch and walked outside before resuming…" : "e.g. I chose to listen first instead of defending…"}
                data-testid={`grat-action-${i}`} />
            </div>
          </div>
        ))}
      </div>

      <button className="bridge-btn" onClick={save} disabled={saving} data-testid="grat-save">
        {saving ? "Saving…" : "Save Today's Gratitude ✦"}
      </button>
      {savedSuccess && (
        <div className="bridge-success-banner" data-testid="grat-saved-banner">
          ✦ Beautiful. Your gratitude has been saved. Come back tomorrow and continue building your record of an intentional, abundant life.
        </div>
      )}

      <div className="grat-history-section">
        <p className="grat-history-label">Your record</p>
        {history.length === 0 ? (
          <p style={{ fontSize: 13.5, color: "var(--bridge-muted)", lineHeight: 1.6 }}>Your gratitude entries will appear here as you build your practice.</p>
        ) : (
          history.slice(0, 12).map((h) => (
            <div className="grat-history-card" key={h.date} data-testid={`grat-history-${h.date}`}>
              <div>
                <div className="grat-history-date">{prettyDate(h.date)}</div>
                <div className="grat-history-count">{(h.entries || []).length} {(h.entries || []).length === 1 ? "gratitude" : "gratitudes"}</div>
              </div>
              {(h.entries || [])[0]?.grateful_for && (
                <div style={{ flex: 1, fontSize: 12.5, color: "var(--bridge-muted)", fontStyle: "italic", lineHeight: 1.5, textAlign: "right", maxWidth: "60%" }}>
                  "{(h.entries || [])[0].grateful_for.slice(0, 90)}"
                </div>
              )}
            </div>
          ))
        )}

        <div className="grat-monthly-cta">
          <p>Want a beautiful PDF summary of your gratitude practice — emailed to you with a personalised reflection on the themes in your entries? You'll receive one automatically on the 1st of each month, or request one anytime.</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="bridge-btn bridge-btn-gold" onClick={() => emailMonthly("current")} disabled={emailingMonth} data-testid="grat-email-current">
              {emailingMonth ? "Sending…" : "✦ Email this month's report"}
            </button>
            <button className="bridge-btn bridge-btn-ghost" onClick={() => emailMonthly("last")} disabled={emailingMonth} data-testid="grat-email-last">
              Email last month's report
            </button>
          </div>
          {monthlyMsg && (
            <div style={{ marginTop: 10, fontSize: 13, color: monthlyMsg.kind === "err" ? "#b91c1c" : "var(--bridge-sage)" }} data-testid="grat-monthly-msg">
              {monthlyMsg.text}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--bridge-muted)", lineHeight: 1.55 }}>
            ✦ Auto-delivery is powered by Resend (your email provider). Kajabi handles your subscriber list — these monthly reports are sent directly from us, separate from any Kajabi email sequences.
          </div>
        </div>
      </div>
    </div>
  );
}
